import { Chart } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { configure } from 'mobx';
import {
    AutoInjectContext,
    CommonAppStore,
    ErrorStore,
    HttpClient,
    PowerBIStore,
    registerTimezonePreferenceStore,
    registerTimezoneStore,
    TasksStore,
    TicketsStore,
    TooltipsStore,
} from 'Mri.Web.Common';
import { UserManagerSettings } from 'oidc-client';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { polyfill } from 'smoothscroll-polyfill';
import '../Styles/app.scss';
import { AccountStore } from './Account/AccountStore';
import { AccountView } from './Account/AccountView';
import { AccountsStore } from './Accounts/AccountsStore';
import { AlertActionsStore } from './AlertActions/AlertActionsStore';
import { DisplayEventProfileStore } from './Alerts/DisplayEventProfileStore';
import { AuditStore } from './Audit/AuditStore';
import { BiStore } from './BI/BiStore';
import { ClientsStore } from './Clients/ClientsStore';
import { ContractsStore } from './Contracts/ContractsStore';
import { App } from './Core/App';
import { AppStore } from './Core/AppStore';
import { LabelStore } from './Dashboard/LabelStore';
import { DisplayFiltersStore } from './DisplayFilters/DisplayFiltersStore';
import { DisplayListViewModel } from './DisplayFilters/DisplayListViewModel';
import { DisplaysChartsStore } from './Displays/DisplaysChartsStore';
import { DisplaysStore } from './Displays/DisplaysStore';
import { DocumentsStore } from './Documents/DocumentsStore';
import { InvitesStore } from './Invites/InvitesStore';
import { ManageStore } from './Manage/ManageStore';
import { UnitsStore } from './Units/UnitsStore';
import { UnsubscribeStore } from './Unsubscribe/UnsubscribeStore';
import { UnsubscribeView } from './Unsubscribe/UnsubscribeView';
import { UsersStore } from './Users/UsersStore';
import { WarrantyAlertsStore } from './WarrantyAlerts/WarrantyAlertsStore';
import { WeatherStore } from './Weather/WeatherStore';
import { createRoot } from 'react-dom/client';
import { EmailsStore } from './Emails/EmailsStore';

//  @TODO Fix Sentry
// Sentry.init({
//     dsn: 'https://19bd6ca4353742cb930e5766141289e7@o4504526605910016.ingest.sentry.io/4505233881038848',
//     integrations: [new Sentry.BrowserTracing(), new Sentry.Replay()],
//     autoSessionTracking: true,
//     // Set tracesSampleRate to 1.0 to capture 100%
//     // of transactions for performance monitoring.
//     // We recommend adjusting this value in production
//     tracesSampleRate: 1.0,
// });

configure({
    enforceActions: 'observed',
    // TODO enable the following
    // computedRequiresReaction: true,
    // reactionRequiresObservable: true,
    // observableRequiresReaction: true,
});
polyfill();

const httpClient = new HttpClient();

const initialOidcConfig = (window as any).initialOidcConfig as UserManagerSettings;
const accountStore = new AccountStore(httpClient, initialOidcConfig);

httpClient.setAccessTokenStore(accountStore);

const accountsStore = new AccountsStore(httpClient);
const alertActionsStore = new AlertActionsStore(httpClient);
const auditStore = new AuditStore(httpClient);
const biStore = new BiStore(httpClient);
const appStore = new AppStore();
const clientsStore = new ClientsStore(httpClient);
const contractsStore = new ContractsStore(httpClient);
const commonAppStore = new CommonAppStore(httpClient);
const displaysChartsStore = new DisplaysChartsStore(httpClient);
const displayEventProfileStore = new DisplayEventProfileStore(httpClient);
const displayFiltersStore = new DisplayFiltersStore(httpClient);
const displaysStore = new DisplaysStore(httpClient, accountStore);
const documentsStore = new DocumentsStore(httpClient);
const emailsStore = new EmailsStore(httpClient);
const errorStore = new ErrorStore(httpClient);
const invitesStore = new InvitesStore(httpClient);
const labelStore = new LabelStore(httpClient);
const manageStore = new ManageStore(httpClient, accountStore);
const powerBIStore = new PowerBIStore(httpClient);
accountStore.SetManageStore(manageStore);
const tasksStore = new TasksStore(httpClient, accountStore);
const ticketsStore = new TicketsStore(httpClient, (window as any).initialFieldIds);
const tooltipsStore = new TooltipsStore(httpClient);
const unitsStore = new UnitsStore(httpClient);
const unsubscribeStore = new UnsubscribeStore(httpClient);
const usersStore = new UsersStore(httpClient);
const warrantyAlertsStore = new WarrantyAlertsStore(httpClient);
const weatherStore = new WeatherStore(httpClient);

registerTimezonePreferenceStore(accountStore);
registerTimezoneStore(displaysStore);

const stores = {
    accountStore,
    accountsStore,
    alertActionsStore,
    appStore,
    auditStore,
    biStore,
    clientsStore,
    commonAppStore,
    contractsStore,
    displaysChartsStore,
    displayEventProfileStore,
    displayFiltersStore,
    displaysStore,
    documentsStore,
    emailsStore,
    errorStore,
    invitesStore,
    labelStore,
    manageStore,
    powerBIStore,
    tasksStore,
    ticketsStore,
    tooltipsStore,
    unsubscribeStore,
    unitsStore,
    usersStore,
    warrantyAlertsStore,
    weatherStore,
};

DisplayListViewModel.stores = stores;

Sentry.setContext('UserStorage', accountStore.userStorage as any);
Sentry.setContext('Displays', displaysStore.displays as any);

const container = document.getElementById('app') as HTMLElement;
const root = createRoot(container);

root.render(
    <AutoInjectContext.Provider value={stores}>
        <BrowserRouter>
            <Switch>
                <Route path="/account" component={AccountView} />
                <Route path="/unsubscribe" component={UnsubscribeView} />
                <Route path="/" component={App} />
            </Switch>
        </BrowserRouter>
    </AutoInjectContext.Provider>
);
